/**
 * « Quand commander ? » — la semaine en dessin.
 *
 * Demande de Philippe (28/09/2026) : les fruits, légumes et produits frais ne se
 * commandent que du mercredi soir au samedi ; le reste, tous les jours ; et les
 * précommandes (pain, sève…) suivent leur propre heure limite. Les clients
 * lisent peu (même constat que pour le pain) : une semaine de lundi à dimanche,
 * trois groupes nettement séparés (couleur, rayons nommés), un repère
 * « maintenant ».
 *
 * Tout est dessiné depuis les réglages (`/shop-config` : `ordering.window`,
 * `preorders`), jamais écrit en dur : si un horaire change, le dessin suit.
 * Les rayons sont nommés par la page (catalogue chargé) ; à défaut (fenêtre
 * « Comment ça marche ? »), un libellé générique.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { fetchShopConfigCached } from '../../hooks/ecommerce/useShopOrdering';
import {
  fenetreDessinee,
  maintenantDansLaSemaine,
  rangLundi,
  JOURS_LUNDI,
  type FenetreHebdo,
} from '../../utils/fenetreCommande';
import { ligneTempsPrecommande, type TypePrecommande } from '../../utils/ligneTempsPrecommande';

const OLIVE = '#4a5c3a';
const TERRACOTTA = '#b45f2e';
const PRUNE = '#7a4b6b';
const CREME = '#f6efe2';
const VIDE = '#eeebe3';
const JOURS_COURTS = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const heure = (h: number) => (h === 0 ? 'minuit' : `${h} h`);
const pct = (x: number) => `${(x * 100).toFixed(3)}%`;

/* ---------- Pictos ---------- */

function Legumes() {
  return (
    <svg width="28" height="28" viewBox="0 0 32 32" aria-hidden className="shrink-0">
      <path d="M8 13 L19 24 C17 27 12 26 9 22 C6 19 6 15 8 13 Z" fill="#e0782c" />
      <path d="M8 13 L5 8 M8 13 L4 12 M8 13 L9 7" stroke="#5e8a3a" strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="22" cy="15" r="7" fill="#c9412e" />
      <path d="M22 8 C22 6 23 5 24.5 4.5" stroke={OLIVE} strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <path d="M22.5 7.5 C25 6 27 7 27.5 8.5 C25.5 9.5 23.5 9 22.5 7.5 Z" fill="#5e8a3a" />
    </svg>
  );
}

function Bocal() {
  return (
    <svg width="28" height="28" viewBox="0 0 32 32" aria-hidden className="shrink-0">
      <rect x="9" y="4" width="14" height="5" rx="1.5" fill={TERRACOTTA} />
      <path d="M8 10 H24 V25 C24 27 22.5 28 21 28 H11 C9.5 28 8 27 8 25 Z" fill={CREME} stroke={OLIVE} strokeWidth="2" />
      <circle cx="13" cy="21" r="1.6" fill="#c8893f" />
      <circle cx="17.5" cy="23" r="1.6" fill="#c8893f" />
      <circle cx="19" cy="18.5" r="1.6" fill="#c8893f" />
      <circle cx="14.5" cy="16.5" r="1.6" fill="#c8893f" />
    </svg>
  );
}

function Pain() {
  return (
    <svg width="22" height="22" viewBox="0 0 32 32" aria-hidden className="shrink-0">
      <g transform="rotate(-35 16 16)">
        <rect x="3" y="11" width="26" height="10" rx="5" fill="#c8893f" />
        <path d="M9 13.5 L11 18.5 M15 13.5 L17 18.5 M21 13.5 L23 18.5" stroke="#ecd6a6" strokeWidth="1.8" strokeLinecap="round" />
      </g>
    </svg>
  );
}

function Bouteille() {
  return (
    <svg width="22" height="22" viewBox="0 0 32 32" aria-hidden className="shrink-0">
      <rect x="13" y="3" width="6" height="5" rx="1" fill={OLIVE} />
      <path d="M12 9 H20 V11 C23 12.5 24 14.5 24 17 V27 C24 28.5 23 29 21.5 29 H10.5 C9 29 8 28.5 8 27 V17 C8 14.5 9 12.5 12 11 Z" fill="#dfeaf0" stroke="#6b8fa3" strokeWidth="1.8" />
      <path d="M16 16 C14 19 13.5 20.5 13.5 22 A2.5 2.5 0 0 0 18.5 22 C18.5 20.5 18 19 16 16 Z" fill="#6b8fa3" />
    </svg>
  );
}

function Panier() {
  return (
    <svg width="22" height="22" viewBox="0 0 32 32" aria-hidden className="shrink-0">
      <path d="M10 13 C10 6 22 6 22 13" stroke={PRUNE} strokeWidth="2.2" fill="none" />
      <path d="M5 13 H27 L24.5 27 H7.5 Z" fill={CREME} stroke={PRUNE} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

/** Petit réveil posé sur l'heure limite. */
function Reveil() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
      <circle cx="8" cy="9" r="6" fill="#fff" stroke={PRUNE} strokeWidth="1.8" />
      <path d="M8 6 V9 L10 10" stroke={PRUNE} strokeWidth="1.6" fill="none" strokeLinecap="round" />
    </svg>
  );
}

const pictoPrecommande = (slug: string) =>
  slug === 'pain' ? <Pain /> : slug.startsWith('seve') ? <Bouteille /> : <Panier />;

/* ---------- Mise en page ---------- */

function Groupe({
  couleur,
  titre,
  picto,
  rayons,
  children,
}: {
  couleur: string;
  titre: string;
  picto: ReactNode;
  rayons: string[];
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl p-2.5" style={{ background: `${couleur}0f`, borderLeft: `4px solid ${couleur}` }}>
      <div className="flex items-center gap-2">
        {picto}
        <span className="text-[13.5px] font-extrabold uppercase tracking-[.04em]" style={{ color: couleur }}>
          {titre}
        </span>
      </div>
      {rayons.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {rayons.map((r) => (
            <span
              key={r}
              className="rounded-full bg-white px-2 py-0.5 text-[12px] font-semibold text-[#3a3324]"
              style={{ border: `1px solid ${couleur}40` }}
            >
              {r}
            </span>
          ))}
        </div>
      )}
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Maintenant({ x, clair = false }: { x: number; clair?: boolean }) {
  return (
    <div
      className="pointer-events-none absolute -top-1 -bottom-1 w-[2px] -ml-px rounded"
      style={{ left: pct(x), background: clair ? CREME : '#1f2a17' }}
      aria-hidden
    />
  );
}

export default function FenetreFrais({
  compact = false,
  rayonsFenetre,
  rayonsToujours,
}: {
  compact?: boolean;
  /** Rayons qui suivent la fenêtre (catalogue chargé). */
  rayonsFenetre?: string[];
  /** Rayons commandables tous les jours. */
  rayonsToujours?: string[];
}) {
  const [fenetre, setFenetre] = useState<FenetreHebdo | null>(null);
  const [precommandes, setPrecommandes] = useState<TypePrecommande[]>([]);
  const [maintenant, setMaintenant] = useState(() => maintenantDansLaSemaine());

  useEffect(() => {
    let annule = false;
    fetchShopConfigCached()
      .then((c) => {
        if (annule) return;
        setFenetre(c.ordering?.window ?? null);
        // Seuls les types qui ont des produits EN VENTE : viande et poisson
        // existent dans les réglages sans aucun produit (28/09/2026).
        setPrecommandes((c.preorders || []).filter((t) => (t.products_count ?? 0) > 0));
      })
      .catch(() => {});
    const t = window.setInterval(() => setMaintenant(maintenantDansLaSemaine()), 60_000);
    return () => {
      annule = true;
      window.clearInterval(t);
    };
  }, []);

  const f = fenetreDessinee(fenetre);
  if (!f) return null;

  const ouvert = f.fin > f.debut ? maintenant >= f.debut && maintenant < f.fin : maintenant >= f.debut || maintenant < f.fin;
  const lignesPrecommande = precommandes
    .map((t) => ({ t, l: ligneTempsPrecommande(t) }))
    .filter((x): x is { t: TypePrecommande; l: NonNullable<ReturnType<typeof ligneTempsPrecommande>> } => !!x.l);

  const fenetreNoms = rayonsFenetre?.length ? rayonsFenetre : ['Fruits', 'Légumes', 'Crémerie', 'Frais'];
  const toujoursNoms = rayonsToujours?.length ? rayonsToujours : ['Épicerie', 'Boissons', 'Hygiène'];

  return (
    <div className={`shop-sans rounded-2xl border border-[#4a5c3a]/20 bg-white ${compact ? 'p-3' : 'p-4 sm:p-5'}`}>
      {!compact && (
        <h3 className="font-bold text-[16px] mb-2" style={{ color: TERRACOTTA }}>
          Quand commander ?
        </h3>
      )}

      {/* Jours de la semaine, communs aux trois groupes */}
      <div className="grid grid-cols-7 gap-[3px] mb-2 px-2.5">
        {f.cases.map((c) => (
          <div key={c.jour} className="text-center text-[11.5px] font-bold uppercase tracking-[.04em] text-[#6b6550]">
            {JOURS_COURTS[c.jour]}
          </div>
        ))}
      </div>

      <div className="space-y-2.5">
        {/* 1. Selon arrivages et récoltes (Philippe, 28/09 : nos légumes viennent aussi du champ) */}
        <Groupe couleur={TERRACOTTA} titre="Selon arrivages et récoltes" picto={<Legumes />} rayons={fenetreNoms}>
          <div className="relative grid grid-cols-7 gap-[3px]">
            {f.cases.map((c) => (
              <div key={c.jour} className="relative h-7 rounded-md overflow-hidden" style={{ background: VIDE }}>
                {c.fin > c.debut && (
                  <div className="absolute inset-y-0" style={{ left: pct(c.debut), right: pct(1 - c.fin), background: TERRACOTTA }} />
                )}
              </div>
            ))}
            <Maintenant x={maintenant} />
          </div>
          <div className="relative h-5 text-[11px] font-bold" style={{ color: TERRACOTTA }}>
            <span className="absolute top-0.5 -translate-x-1/2 whitespace-nowrap" style={{ left: pct(f.debut) }}>
              {heure(f.ouverture.heure)}
            </span>
            <span className="absolute top-0.5 -translate-x-1/2 whitespace-nowrap" style={{ left: pct(f.fin) }}>
              {heure(f.fermeture.heure)}
            </span>
          </div>
          <p className="text-[12px] text-[#5a5440] leading-snug">
            Du {JOURS[f.ouverture.jour]} {heure(f.ouverture.heure)} au {JOURS[f.fermeture.jour]} {heure(f.fermeture.heure)},
            quand les rayons sont réapprovisionnés.
          </p>
        </Groupe>

        {/* 2. Tous les jours */}
        <Groupe couleur={OLIVE} titre="Tous les jours" picto={<Bocal />} rayons={toujoursNoms}>
          <div className="relative h-7 rounded-md flex items-center justify-center text-[12px] font-bold text-white" style={{ background: OLIVE }}>
            à toute heure
            <Maintenant x={maintenant} clair />
          </div>
        </Groupe>

        {/* 3. Sur précommande */}
        {lignesPrecommande.length > 0 && (
          <Groupe couleur={PRUNE} titre="Sur précommande" picto={<Panier />} rayons={[]}>
            <div className="space-y-2.5">
              {lignesPrecommande.map(({ t, l }) => {
                const retraits = new Set(l.etapes.flatMap((e) => e.joursRetrait));
                const limites = l.etapes.map((e) => ({
                  // Bornée aux bords : une limite au lundi 0 h (sève) sortait du cadre.
                  x: Math.min(0.985, Math.max(0.015, (rangLundi(e.jourLimite) * 24 + e.heureLimite) / (7 * 24))),
                  e,
                }));
                const e0 = l.etapes[0];
                // « avant lundi minuit » se lit « lundi soir » : à 0 h, on nomme la veille.
                const limiteTexte =
                  t.cutoff_mode === 'RELATIVE'
                    ? `la veille avant ${heure(e0.heureLimite)}`
                    : e0.heureLimite === 0
                      ? `avant ${JOURS[(e0.jourLimite + 6) % 7]} minuit`
                      : `avant ${JOURS[e0.jourLimite]} ${heure(e0.heureLimite)}`;
                return (
                  <div key={t.slug}>
                    <div className="flex items-start gap-1.5 text-[12.5px] leading-snug text-[#3a3324]">
                      {pictoPrecommande(t.slug)}
                      <span className="min-w-0 pt-0.5">
                        <strong className="whitespace-nowrap">{t.name}</strong>
                        <span className="text-[#5a5440]"> · commander {limiteTexte}</span>
                      </span>
                    </div>
                    <div className="relative mt-1 grid grid-cols-7 gap-[3px]">
                      {JOURS_LUNDI.map((j) => (
                        <div
                          key={j}
                          className="h-6 rounded-md"
                          style={{ background: retraits.has(j) ? `${PRUNE}cc` : VIDE }}
                        />
                      ))}
                      {limites.map(({ x }, i) => (
                        <div key={i} className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: pct(x) }}>
                          <Reveil />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-[#5a5440]">
                <span className="flex items-center gap-1.5">
                  <Reveil /> heure limite pour commander
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-3 w-5 rounded" style={{ background: `${PRUNE}cc` }} aria-hidden /> jour de retrait
                </span>
              </p>
            </div>
          </Groupe>
        )}
      </div>

      <p className="mt-3 flex items-center gap-2 text-[12.5px] text-[#4a4530]">
        <span className="inline-block h-3.5 w-[2px] rounded" style={{ background: '#1f2a17' }} aria-hidden />
        {ouvert ? (
          <span>
            <strong>Maintenant</strong> : tout se commande.
          </span>
        ) : (
          <span>
            <strong>Maintenant</strong> : le frais revient {JOURS[f.ouverture.jour]} à {heure(f.ouverture.heure)} ; le reste se
            commande.
          </span>
        )}
      </p>
    </div>
  );
}
